import { useState, useEffect, useMemo } from 'react';
import * as Yup from 'yup';

const useValidationYup = <T>(
  schema: Yup.Schema<T>,
  values: T,
  localeObject?: Record<string, string>,
) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showError, setShowError] = useState<boolean>(true);

  const isValid = useMemo(() => !Object.keys(errors).length, [errors]);

  const generateErrorKey = (...args: (string | number)[]): string => {
    return args
      .map((arg, index) =>
        typeof arg === 'number' ? `[${arg}]` : index > 0 ? `.${arg}` : arg,
      )
      .join('');
  };

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(values, { abortEarly: false });
        setErrors({});
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const formattedErrors: Record<string, string> = {};
          err.inner.forEach((error) => {
            if (error.path) {
              formattedErrors[error.path] =
                localeObject?.[error.message] || error.message;
            }
          });
          setErrors(formattedErrors);
        }
      }
    };

    validate();
  }, [localeObject, schema, values]);

  return {
    errors: showError ? errors : {},
    isValid,
    setErrors,
    generateErrorKey,
    setShowError,
  };
};

export default useValidationYup;
